// © Microsoft Corporation. All rights reserved.

import {
    AppError,
    ColumnArrangement,
    ExperienceInitialPositionProps,
    ExperienceInitialProps,
    Telemetry,
    TelemetryProps,
    TelemetryTags,
    TelemetryTypes,
    fastBreakpointManager
} from "@msnews/experiences-base";
import {
    BaseExperienceConfig,
    ChildArrangementMetadata,
    ChildExperienceConfigInfo,
    ChildExperiencesDeferRenderType,
    ChildInfo,
    ChildrenTypes,
    CustomCompositionConfig,
    ExperienceCompositionTypes,
    ExperienceState,
    GridCompositionConfig,
    LayoutConfig,
    PeregrineChildInfo,
    ScreenTypeMetadata,
    ScreenWidth,
    TelemetryBase,
    appEnvironment,
    resolver
} from "@msnews/core";
import {
    CommonActions,
    DataConnector,
    DynamicReducer,
    IActionPayload,
    IReducer,
    PageBase,
    dataConnectorInjector
} from "@msnews/experiences-redux";
import { ComponentConnectorAppErrors, logger } from "@msnews/diagnostics";
import { DeferralEventType, ExperienceMilestoneEventTypes, MilestoneDataActions, MilestoneDataConnector, getExperienceLoadDelegate, getExperienceRenderDelegate } from "@msnews/milestone-data";
import { Dispatch, Store } from "redux";

import { ConfigProcessor } from "@msnews/config-validator";
import { ConnectorNamespaces } from "@msnews/experiences-constants";
import { ExperienceLoadInfo } from "./ExperienceLoadInfo";
import { ExperienceLoader } from "..";
import { IComponentConnector } from "./IComponentConnector";
import React from "react";
import { canUseDOM } from "@msnews/isomorphic-util";
import { connect } from "react-redux";
import { getMilestoneDataConnector } from "@msnews/milestone-data";
import { instrumentPerf } from "./ExperiencePerfTimeline";
import { isEmpty } from "lodash-es";
import { isFunction } from "util";
import { subspaced } from "react-redux-subspace";

export type mapToPropsSignature<TState, TProps> = (state: TState, ownProps?: TProps) => TProps;
export type mapStateToPropsReturnType<TState, TProps> = TProps | mapToPropsSignature<TState, TProps>;
export type mapDispatchToPropsReturnType<TState, TProps> = TProps | mapToPropsSignature<TState, TProps>;


/**
 * Map for making config-less data connectors faster to load
 *
 * HACK: HACK: HACK: This is a temporary hack until we can update CMS configs to remove
 *  the HREF requirement for config-less connectors, so that we can opt out
 *  of loading configs without having this special check. Need to do in phases
 *  so that we're not breaking PROD users with old scripts.
 */
export const ConfiglessConnectors = {
    [ConnectorNamespaces.BingImageData]: true,
    [ConnectorNamespaces.OneServiceContent]: true,
    [ConnectorNamespaces.TelemetryData]: true,
    [ConnectorNamespaces.TopicData]: true,
    [ConnectorNamespaces.PageClickTrackerData]: true
};

/**
 * Base class for a component connector. Every connected component in an app has a connector that inherits from this class
 * @class
 */
export abstract class ComponentConnector<TConfig, TState, TProps, TParentState>
    extends DataConnector<TConfig, TState, TParentState>
    implements IComponentConnector<TState, TProps, TParentState> {

    /**
     * A map of child connectors. The key to the map is a concatenation: ExperienceType_ExperienceInstanceId.
     * The ExperienceType is the the same as the ExperienceId in the @msnews/experiences-constants ExperienceIds enum (i.e. River)
     * The ExperienceInstanceId is set in config, as part of the parent experience config. Which children get created and rendered
     * by a parent experience will be specific to that experience, so it will be up to the parent experience to determine the
     * instanceId of the children to be rendered by looking in its config object.
     * @type Map<string, IComponentConnector<any, any, any>>
     */
    protected childMap?: Map<string, IComponentConnector<any, any, any>> = new Map();

    /**
     * This experiences telemetry props, derived from the parent telemetry props
     */
    protected telemetryProps: TelemetryProps = {};

    /**
     * The telemetry tags to pass along as unhandled props for rendering
     */
    protected telemetryTags: TelemetryTags = {};

    /**
     * The group the experience belongs to, if applicable. This property is to be set as needed in the specific experience connectors
     */
    protected $experienceGroup: string = undefined;

    /**
     * The connected rendering component for the experience connector
     */
    private $connectedComponent: React.ComponentClass<TProps> = undefined;

    /**
     * The connected rendering component ReactElement instance for the experience connector
     */
    private $componentInstance: React.ReactElement<any> = undefined;

    /**
     * The connector props used to connector this connector to an experience
     */
    private $connectorProps: ExperienceInitialProps;

    /**
     * flag indicating if we should render when offline. Default is false
     */
    private $renderIfOffline: boolean = false;

    /**
     * The experience type
     */
    private $experienceType: string = undefined;

    /**
     * failsafe timeout(ms) to render child experiences based on milestone batch load strategy
     */
    private $maxWaitToBatchRenderChildExperiences: number = 4000;

    /**
     * The milestone data connector (if it exists)
     */
    private $milestoneDataConnector: MilestoneDataConnector = undefined;

    /**
     * Gets the connected component for this experience
     */
    public get connectedComponent(): React.ComponentClass { return this.$connectedComponent; }

    /**
     * Gets the component instance for this experience. This is used for rendering the experience
     */
    public get componentInstance(): React.ReactElement<any> { return this.$componentInstance; }

    /**
     * Gets the experience type for this experience.
     */
    public get experienceType(): string { return this.$experienceType; }

    /**
     * Sets the component instance for this experience
     */
    public set componentInstance(value: React.ReactElement<any>) { this.$componentInstance = value; }

    /**
     * Sets the renderIfOffline flag
     */
    public set renderIfOffline(value: boolean) { this.$renderIfOffline = value; }

    /**
     * Get a map of the child experience component instances, which is appropriate for passing into the
     * inspector function as part of mapStateToProps
     */
    public get childComponentInstanceList(): { [instanceId: string]: React.ReactElement<any>; } {
        const childComponentList: { [instanceId: string]: React.ReactElement<any>; } = {};
        this.childMap.forEach((connector, key) => {
            childComponentList[key] = connector.componentInstance;
        });
        return childComponentList;
    }

    /**
     * Construct a new Component connector
     * @constructor
     * @param { string } namespace The redux subspace namespace for the connector
     * @param { string } parentNamespace The redux subspace namespace for the parent connector, if it exists
     * @param { DynamicReducer } rootReducer The root reducer for the Application
     * @param { IReducer<TState> } reducer The reducer for this connector instance
     * @param { Store<TParentState>} parentStore The parent store (or redux subspace) that this connector will be a subspace within
     * @param { TConfig } config Optional param containing the config for the connector
     */
    public constructor(namespace: string,
        parentNamespace: string,
        rootReducer: DynamicReducer,
        reducer: IReducer<TState>,
        parentStore: Store<TParentState>,
        config?: TConfig
    ) {
        super(namespace, parentNamespace, rootReducer, reducer, parentStore, config);
        this.selectComponentState = this.selectComponentState.bind(this);

        // To get the experience type, split the namespace at the first "_" and use the first substring
        // as the type. The convention for namespace is: ExperienceType_configHref_InstanceId. If there is no instanceId, then then namespace is just: ExperienceType
        const namespaceArray = namespace.split("_");
        this.$experienceType = namespaceArray[0];
        // configNamespace includes experienceType + index path
        const configNamespace = (namespaceArray.length > 1) ? this.$experienceType + namespaceArray[1] : this.$experienceType;

        this.config = ConfigProcessor.processConfig(this.config, configNamespace) as TConfig;
    }

    /**
     * Redux mapper used to map the current state to props the component use to render itself.
     * @method
     * @param {TState} state The current state of the component.
     * @param {any} ownProps The set of properties that is passed in from where the component is created.
     * This parameter should be not nil only when the component is supposed to have part of its properties coming from a parent component.
     * It can be omitted when the entire set of props is derived from the state.
     * @returns {mapStateToPropsReturnType<TState, TProps>} It can either return the set of mapped props as an object of type TProps derived from a state
     * object of type TState or a function that will be used to map the state to the props.
     */
    public abstract mapStateToProps(state: TState, ownProps?: any): mapStateToPropsReturnType<TState, TProps>;

    /**
     * Inject into props redux action creators.
     * @method
     * @param {Dispatch} dispatch The redux dispatch function.
     * @param {any} ownProps The set of properties that is passed from where the component is created.
     * This parameter should be not nil only when the component is supposed to have part of its properties coming from a parent component.
     * It can be omitted when the entire set of props is derived from the state.
     * @returns {TProps} An enriched set of props.
     */
    public mapDispatchToProps(dispatch: Dispatch<IActionPayload>, ownProps?: any): mapDispatchToPropsReturnType<TState, TProps> {
        return {} as TProps;
    }

    /**
     * Overrides the default redux mergeProps function that will add a callback function for reporting experience milestone timing
     * @method
     * @param {TState} stateProps The set of properties from mapStateToProps.
     * @param {TProps} dispatchProps The set of properties from mapDispatchToProps.
     * @param {any} ownProps The set of properties that is passed from where the component is created.
     * @returns {TProps} The merged set of props.
     */
    public mergeProps(stateProps: TProps, dispatchProps: TProps, ownProps: any): TProps {
        // WARNING WARNING WARNING. Some render engines, like preact will take dependencies on some props that may be set to undefined, so the
        // merge must preserve properties that are undefined in the source (i.e stateProps) in the result.
        // Specific to preact depends on the constructor property being explicity set to undefined. When an element is created by preact
        // the constructor property is set to undefined, and that must be preserved by this merge function.
        // Avoid using lodash merge, as it does not preserve the undefined properties

        // If we are in Mns Creator Dashboard mode, then set props for the config href, and experience type. These will get rendered
        // on each experience as unhandled props
        let mnsDashBoardProps = {};
        if (appEnvironment.ShowMnsCreatorDashboard) {
            mnsDashBoardProps = {
                "data-config-href": this.configHref,
                "data-experience-type": this.experienceType
            };
        }

        // The onExperienceMilestonTimingUpdate prop is set here, so that it is injected into the resulting properties for all experiences.
        return {
            onExperienceMilestoneTimingUpdate: this.onExperienceMilestoneTimingUpdate,
            ...mnsDashBoardProps,
            ...ownProps,
            ...stateProps,
            ...dispatchProps
        };
    }

    /**
     * Connects the passed in component to the redux store/state of the connector
     * @method
     * @param {React.ComponentType<TProps>} componentToConnect The react element to connect.
     * @param {any} connectorProps Optional connector props to pass to the onComponentConnect call.
     * @returns {React.ComponentClass<any>} The Redux connected component.
     */
    public async connectComponent(componentToConnect: React.ComponentType<TProps>, connectorProps?: ExperienceInitialProps): Promise<React.ComponentClass<TProps>> {

        // If we are not online, and we should not render offline, then just return undefined. This will prevent the experience from ever
        // rendering
        if (canUseDOM() && !navigator.onLine && !this.$renderIfOffline) {
            return undefined;
        }

        const connectMeasureEnd = this.perfTimeline && this.perfTimeline.startMeasure("connect").endMeasure;

        // Call the onComponentConnect method. This is done first, so that the connector can see the initialProps before mapStateToProps is called
        // onComponentConnect is async, so we need to wait for it to complete first
        await this.onComponentConnect(connectorProps);

        // if this experience should be instrumented for perf, do so and get the perf HOC
        if (this.perfTimeline) {
            componentToConnect = instrumentPerf(componentToConnect, this.perfTimeline);
            this.mapStateToProps = this.perfTimeline.instrumentFunc("mapStateToProps", this.mapStateToProps, this);
            this.mapDispatchToProps = this.perfTimeline.instrumentFunc("mapDispatchToProps", this.mapDispatchToProps, this);
            this.mergeProps = this.perfTimeline.instrumentFunc("mergeProps", this.mergeProps, this);
        }

        // Connect the component to redux (will cause mapStateToProps to be called)
        const connectedComponent: React.ComponentClass<any> = connect(
            this.mapStateToProps.bind(this),
            this.mapDispatchToProps.bind(this),
            this.mergeProps.bind(this),
            { pure: true }
        )(componentToConnect);

        // Wrap the redux connected component in a Subspace Provider, and set that as the connectedComponent for this connector
        this.$connectedComponent = subspaced(this.selectComponentState, this.namespace)(connectedComponent);

        if (connectMeasureEnd) {
            connectMeasureEnd();
        }

        return this.$connectedComponent;
    }

    /**
     * This method reads the experience config and adds any ordinal(index) based child experiences specified in the config to the input
     * ChildExperienceConfigInfo array at the specified ordinal position defined in config.
     * @method
     * @param {ExperienceLoadInfo[]} ExperienceLoadInfoList Array of ChildExperienceConfigInfo objects
     * @param {number} mergeStartPosition MergeStartPosition represents the start position for the merge. If your experience only need to call this once,
     * it should be 1. For deep river kind of scenario, you would need to pass the updated merge start position. StartPosition = lastEndPosition + 1
     * @returns {ExperienceLoadInfo[]}
     */
    public mergeOrdinalChildExperiences(experienceInfoList: ExperienceLoadInfo[], mergeStartPosition: number,
        callback?: (experienceInfo: ExperienceLoadInfo) => void): ExperienceLoadInfo[] {
        if (!experienceInfoList) {
            return null;
        }

        if (mergeStartPosition < 1) {
            TelemetryBase.trackAppErrorEvent({
                ...ComponentConnectorAppErrors.ListStartPositionInvalid,
                message: "listStartFrom must be greater than 0."
            });

            return experienceInfoList;
        }

        const config = this.config as BaseExperienceConfig<any>;

        if (!config.additionalChildExperiences || config.additionalChildExperiences.length === 0) {
            return experienceInfoList;
        }

        const ordinalBlockForCurrentColumn = this.getOrdinalObjectForCurrentBreakpoint<ChildArrangementMetadata>(config.additionalChildExperiences);
        let mergeEndPos = mergeStartPosition + experienceInfoList.length;

        // Following code loops through the ChildArrangementMetadata array and inserts an additional child experience if startPosition provided in config is greater than mergeStartPosition
        // and less than mergeEndPos which is mergeStartPosition + length of the input childConfigInfoList
        ordinalBlockForCurrentColumn.children.forEach((childArrangementMetadata) => {
            const childExpArrangement = childArrangementMetadata.arrangement;

            // TS requires to define this explicitly and then assign it in line 234.
            const positionProps: ExperienceInitialPositionProps = { experienceArrangement: childExpArrangement };

            if (childExpArrangement && childExpArrangement.startPosition >= mergeStartPosition && childExpArrangement.startPosition <= mergeEndPos) {
                const insertAt = childExpArrangement.startPosition - mergeStartPosition;
                const experienceInfo: ExperienceLoadInfo = {
                    configInfo: childArrangementMetadata.childExperienceConfigInfo,
                    initialProps: positionProps
                };

                experienceInfoList.splice(insertAt, 0, experienceInfo);
                mergeEndPos++;

                if (typeof callback === "function") {
                    callback(experienceInfo);
                }
            }
        });

        return experienceInfoList;
    }

    /**
     * This method is called when a component is connected to the connectors' store.
     * This method can be overridden and  used by connectors to perform one time initialization functions
     * @method
     * @param {any} connectorProps Optional initial props passed into the onComponentConnect call.
     * @returns {void}
     */
    public async onComponentConnect(connectorProps?: ExperienceInitialProps): Promise<void> {

        // save the last connected connector props
        this.$connectorProps = connectorProps;

        if (connectorProps) {

            // Setup telemetry props for this experience.
            if (connectorProps.parentTelemetry
                && !isEmpty(connectorProps.parentTelemetry)) {

                // Generate the telemetry props for this experience. These will also be the parent props for any child experiences
                this.telemetryProps = Telemetry.getChildTelemetryItem(
                    connectorProps.parentTelemetry,
                    this.$experienceType,
                    TelemetryTypes.Module
                );

                // generate the telemetry tags to pass along for rendering
                this.telemetryTags = Telemetry.render(this.telemetryProps);
            }

            // Override the onAppError function if passed one.
            if (isFunction(connectorProps.onAppErrorCallback)) {
                this.onAppError = connectorProps.onAppErrorCallback;
            }
        }

        // load data connectors
        // Experiences relying on looking up data connectors need await here. Data Connectors should not have any blocking calls
        // in the constructor.
        await this.loadDataConnectors();

        for (let injectedDataConnector of this.injectedDataConnectors) {
            // Setup the initial state
            CommonActions.dataConnectorMergeInit.getActionSender(this).send(injectedDataConnector);

            // Register the merge so we get updates
            dataConnectorInjector.registerInjection(this.fullNamespace, injectedDataConnector);
        }

        // load up child experiences
        await this.onLoadChildExperiences();
    }

    /**
     * Gets a new instance of a ReactElement for the connected Component for this connector, that can be used to render
     * the experience.
     * This method will replace any existing ReactElement instance that has been previously created.
     * @param ownProps optional props to be passed into the connected components' mapStateToProps method
     */
    public getNewComponentInstance(ownProps?: TProps): React.ReactElement<TProps> {
        // If there is a connected component for this connector, then create an instance of it.
        if (this.$connectedComponent) {
            const newProps = { ...ownProps, key: this.namespace };
            this.$componentInstance = React.createElement(this.$connectedComponent, newProps);
        }

        return this.$componentInstance;
    }

    /**
     * Report a milestone timing event
     * @param milestoneType The type of milestone timing is being reported for
     * @param startTime The start time (if defined)
     * @param endTime  The end time (if defined)
     */
    protected onExperienceMilestoneTimingUpdate = (milestoneType: ExperienceMilestoneEventTypes,
        startTime?: number,
        endTime?: number) => {

        // Do a onetime check to get the Milestone Data Connector instance
        if (this.$milestoneDataConnector === undefined) {
            this.$milestoneDataConnector = getMilestoneDataConnector();
            if (this.$milestoneDataConnector === undefined) {
                this.$milestoneDataConnector = null;    // Set it explicitly to null, so that we don't try to keep getting it
                return;
            }
        }

        // Skip the Milestone update if we don't have a Milestone Data connector
        if (!this.$milestoneDataConnector) {
            return;
        }

        switch (milestoneType) {
            case ExperienceMilestoneEventTypes.domComplete:
                if (endTime) {
                    MilestoneDataActions.updateDomCompleteTiming.getActionSender(this.$milestoneDataConnector).send(
                        this.$experienceType,
                        this.$experienceGroup,
                        this.namespace,
                        startTime,
                        endTime);
                } else {
                    MilestoneDataActions.updateDomCompleteTiming.getActionSender(this.$milestoneDataConnector).send(
                        this.$experienceType,
                        this.$experienceGroup,
                        this.namespace,
                        startTime);
                }
                break;

            case ExperienceMilestoneEventTypes.initialized:
                if (endTime) {
                    MilestoneDataActions.updateInitTiming.getActionSender(this.$milestoneDataConnector).send(
                        this.$experienceType,
                        this.$experienceGroup,
                        this.namespace,
                        startTime,
                        endTime);
                } else {
                    MilestoneDataActions.updateInitTiming.getActionSender(this.$milestoneDataConnector).send(
                        this.$experienceType,
                        this.$experienceGroup,
                        this.namespace,
                        startTime);
                }
                break;

            case ExperienceMilestoneEventTypes.rendered:
                if (endTime) {
                    MilestoneDataActions.updateRenderTiming.getActionSender(this.$milestoneDataConnector).send(
                        this.$experienceType,
                        this.$experienceGroup,
                        this.namespace,
                        startTime,
                        endTime);
                } else {
                    MilestoneDataActions.updateRenderTiming.getActionSender(this.$milestoneDataConnector).send(
                        this.$experienceType,
                        this.$experienceGroup,
                        this.namespace,
                        startTime);
                }
                break;

            case ExperienceMilestoneEventTypes.visuallyReady:
                if (endTime) {
                    MilestoneDataActions.updateVisuallyReadyTiming.getActionSender(this.$milestoneDataConnector).send(
                        this.$experienceType,
                        this.$experienceGroup,
                        this.namespace,
                        startTime,
                        endTime);
                } else {
                    MilestoneDataActions.updateVisuallyReadyTiming.getActionSender(this.$milestoneDataConnector).send(
                        this.$experienceType,
                        this.$experienceGroup,
                        this.namespace,
                        startTime);
                }
                break;

            default:
                // do nothing for the default cause, since there is no default timing that can be updated
                logger.log(`Milestone timing update was attempted for an unknown milestone type: ${milestoneType}`);
                break;
        }
    }

    /**
     * This method reads the experience config and returns child experiences placed using index based position.
     * @method
     * @returns {ExperienceLoadInfo[]}
     */
    protected getOrdinalChildExperiences(): ExperienceLoadInfo[] {
        const config = this.config as BaseExperienceConfig<any>;

        if (!config.additionalChildExperiences || config.additionalChildExperiences.length === 0) {
            return null;
        }

        let result: ExperienceLoadInfo[] = [];
        const ordinalBlockForCurrentColumn = this.getOrdinalObjectForCurrentBreakpoint(config.additionalChildExperiences);

        ordinalBlockForCurrentColumn.children.forEach((childArrangementMetadata) => {
            const childExpArrangement = childArrangementMetadata.arrangement;

            // TS requires to define this explicitly and then assign it in line 234.
            const positionProps: ExperienceInitialPositionProps = { experienceArrangement: childExpArrangement };
            result.push({
                configInfo: childArrangementMetadata.childExperienceConfigInfo,
                initialProps: positionProps
            });
        });

        return result;
    }

    /**
     * Loads all the children experiences defined under the configuration in parallel.
     * @param config - the base experience config containing the children config info
     * @param connectorProps - the initial experience connector props
     * @returns {Promise{void}[]} - an array of promises that loads all the children experiences.
     */
    protected async onLoadChildExperiences(): Promise<void> {

        const baseExperienceConfig = this.config as BaseExperienceConfig<any>;
        const childrenConfigInfo: ChildExperienceConfigInfo[] = [];

        // Determine if there are any children in the composition portion of the config document.
        if (baseExperienceConfig && baseExperienceConfig.composition) {

            switch (baseExperienceConfig.composition.type) {
                case ExperienceCompositionTypes.horizontalStack:
                case ExperienceCompositionTypes.layout: {

                    // Iterate over the composition config to find the children
                    const layoutConfig = baseExperienceConfig.composition as LayoutConfig;

                    // Iterate over the composition config to find the children
                    layoutConfig.page.children.forEach(grid => {

                        // If the grid does not contain children, then there are no child experiences
                        if (grid.children === undefined) {
                            return;
                        }

                        // Iterate over the grid children (each one is a column) and see if there is a child experience
                        grid.children.forEach((column) => {

                            // If the columnProps does not contain children, then there are no child experiences
                            if (!column.children) {
                                return;
                            }

                            // If the column.children is a legacy ColumnChildInfo, or of type PeregrineExperience, then
                            // add the child to the list
                            const { childType } = column.children as ChildInfo;
                            if (!childType) {
                                childrenConfigInfo.push(column.children as ChildExperienceConfigInfo);
                            } else if (childType === ChildrenTypes.peregrineExperience) {
                                childrenConfigInfo.push((column.children as PeregrineChildInfo).child);
                            }
                        });
                    });
                    break;
                }

                case ExperienceCompositionTypes.grid: {

                    // Iterate over the grid composition config to find the peregrine child experiences to load
                    const gridRows = (baseExperienceConfig.composition as GridCompositionConfig).grid.rows;
                    gridRows.forEach(row => {

                        // If the row does not contain columns, then there are no child experiences
                        if (row.columns === undefined) {
                            return;
                        }

                        // Iterate over the grid columns and see if there are any items
                        row.columns.forEach((column) => {
                            // If the column.item is a PeregrineExperience, then add the child to the list of children experiences to load
                            const { itemType } = column.item;
                            if (itemType && itemType === ChildrenTypes.peregrineExperience) {
                                childrenConfigInfo.push(column.item.child as ChildExperienceConfigInfo);
                            }
                        });
                    });
                    break;
                }

                case ExperienceCompositionTypes.customExperience: {
                    const customConfig = baseExperienceConfig.composition as CustomCompositionConfig;
                    childrenConfigInfo.push(customConfig.experience);

                    break;
                }

                // No children to load, if we do not know what type of composition this experience has
                default:
            }
        }

        // Load all the children.
        // TODO: passing in the parent's connector props to children verbatim seems wrong.
        //  However experiences such as spotlight seems to be loading it using this method by
        //  passing child specific initial props from all the way from the top level page experience,
        //  so need to resolve that before removing the this.$connectorProps parameter.
        await this.loadChildrenExperiences(childrenConfigInfo, this.$connectorProps);
    }

    /**
     * Load a list of child experiences
     * @param children - The list (array) of child experiences to be loaded
     * @param childConnectorProps - The connector props to pass to each child experience as it is loaded
     * @returns {Promise{void}} - the async operation that denotes loading of all the children.
     */
    protected async loadChildrenExperiences(children: ChildExperienceConfigInfo[], childConnectorProps?: ExperienceInitialProps): Promise<void> {
        if (!children || children.length === 0) {
            return;
        }

        let childExpsToLoadImmediately = [];

        const reRenderCallback = () => {
            this.readyExperienceToRender(children);
        };

        for (const child of children) {
            let promiseDependency = this.startProcessingChildExperience(child, reRenderCallback, childConnectorProps);
            childExpsToLoadImmediately.push(promiseDependency);
        }

        // NOTE: In order to make sure that the fail-fast behavior of Promise.all doesn't kick in, we're wrapping the individual promises
        // with a catch block and ensuring that they always resolve (never reject/throw error). If we don't do this, then
        // even if one child promise gets rejected early, then the parent experience might fail to load the remaining children.
        await Promise.all(childExpsToLoadImmediately);
    }

    /**
     * Gets the list of data connectors to inject into this experience.
     */
    protected get injectedDataConnectors(): ConnectorNamespaces[] {
        return [];
    }

    /**
     * Load a list of data connectors provided in the experience config
     * @returns {Promise{void}} - the async operation that denotes loading of all the children.
     */
    protected async loadDataConnectors(): Promise<void> {
        const baseExperienceConfig = this.config as BaseExperienceConfig<any>;

        // Load any data connectors defined in the config
        if (baseExperienceConfig && baseExperienceConfig.dataConnectors && baseExperienceConfig.dataConnectors.length > 0) {
            const dataConnectorForCurrentScreenType = this.getOrdinalObjectForCurrentBreakpoint<ChildExperienceConfigInfo>(baseExperienceConfig.dataConnectors);

            if (dataConnectorForCurrentScreenType && dataConnectorForCurrentScreenType.children && dataConnectorForCurrentScreenType.children.length > 0) {
                await Promise.all(
                    dataConnectorForCurrentScreenType.children.map(connector =>
                        this.loadDataConnector(connector)));
            }
        }
    }

    /**
     * Loads a single child experience with the given connector initial props.
     * @param configInfo - the child experience config info
     * @param childConnectorProps - optional initial props to pass to connector.
     * @returns {Promise{void}} - the async operation that denotes loading of the child.
     */
    protected async loadChildExperience(configInfo: ChildExperienceConfigInfo,
        childConnectorProps?: ExperienceInitialProps): Promise<void> {
        
        // If the experience is delay loaded, load it through the same flow as composition ones so that MilestoneData deferred loading logic is intact.
        const milestoneDependencyToLoadChildExp = getExperienceLoadDelegate(configInfo.experienceType);
        if (milestoneDependencyToLoadChildExp) {
            await this.loadChildrenExperiences([configInfo], childConnectorProps);
        } else {
            let childConnector = await this.getChildExperienceConnector(configInfo, childConnectorProps);

            if (!childConnector) {
                configInfo.state = ExperienceState.Failed;
                return;
            }

            this.addConnectorToChildMap(configInfo, childConnector);
        }
    }

    /**
     * Provides the connector props to be passed into the onComponentConnect method for any children that are created for this
     * experience connector
     * @param childExperienceType The experience Type of the child experience being loaded
     * @param childExperienceInstanceId The experienceInstance Id of the child experience being loaded
     */
    protected getChildExperienceConnectorProps(childExperienceType: string, childExperienceInstanceId: string): ExperienceInitialProps {
        return (
            {
                parentTelemetry: this.telemetryProps,
                context: this.$connectorProps && this.$connectorProps.context,
                onAppErrorCallback: this.onAppError
            });
    }

    /**
     * This method returns the ScreenTypeMetadata for current breakpoint.
     * @method
     * @param {ScreenTypeMetadata<T>[]} screenTypeObjList Array of ScreenTypeMetadata objects
     * @returns {ScreenTypeMetadata<T>} returns the matching ScreenTypeMetadata object for current breakpoint.
     */
    protected getOrdinalObjectForCurrentBreakpoint<T>(screenTypeObjList: ScreenTypeMetadata<T>[]): ScreenTypeMetadata<T> {
        switch (fastBreakpointManager().currentColumnArrangement) {
            case ColumnArrangement.c1: {
                return this.getMetadataByScreenWidth(ScreenWidth.OneColumn, screenTypeObjList);
            }
            case ColumnArrangement.c2: {
                return this.getMetadataByScreenWidth(ScreenWidth.TwoColumn, screenTypeObjList);
            }
            case ColumnArrangement.c3: {
                return this.getMetadataByScreenWidth(ScreenWidth.ThreeColumn, screenTypeObjList);
            }
            case ColumnArrangement.c4: {
                return this.getMetadataByScreenWidth(ScreenWidth.FourColumn, screenTypeObjList);
            }
            default: {
                return this.getMetadataByScreenWidth(ScreenWidth.Any, screenTypeObjList);
            }
        }
    }

    /**
     * function to call an action to reRender experience
     */
    protected reRenderExperience(): void {
        CommonActions.renderExperience.getActionSender(this).send();
    }

    /**
     * The optional function for Experiences to call use when an error occurs.
     * Defined as empty function so clients don't need to check it.
     * Passed to child experiences. Used mainly outside of Peregrine. e.g. Office page.
     * NOTE: we need to default to track.trackAppErrorEvent for PH components.
     */
    protected onAppError: ((error: AppError) => void) = (err) => TelemetryBase.trackAppErrorEvent(err);

    /**
     * Helper function to match screenWidth and return the correct ScreenTypeMetadata object
     * @method
     * @param {ScreenWidth} screenWidth - screenWidth to match
     * @param {ScreenTypeMetadata[]} screenTypeObjList - Array of ScreenTypeMetadata objects
     * @returns {ScreenTypeMetadata} returns the matching ScreenTypeMetadata object for given screenWidth.
     */
    private getMetadataByScreenWidth<T>(screenWidth: ScreenWidth, screenTypeObjList: ScreenTypeMetadata<T>[]): ScreenTypeMetadata<T> {

        let result =
            screenTypeObjList.find(obj =>
                obj.screenWidth === screenWidth
            )
            || screenTypeObjList.find(obj =>
                obj.screenWidth === ScreenWidth.Any
            );

        return result;
    }

    /**
     * Initializes connector and connected component for a given child experience.
     * @param configInfo - the child experience config info
     * @param childConnectorProps - optional initial props to pass to connector.
     * @returns {Promise<IComponentConnector<any,any,any>>} - the async operation that returns the connector for child experience.
     */
    private async getChildExperienceConnector(
        configInfo: ChildExperienceConfigInfo,
        childConnectorProps?: ExperienceInitialProps): Promise<IComponentConnector<any, any, any>> {

        try {
            // Get an experience loader instance for the child experience
            const { instanceId, href, experienceType } = configInfo;
            const loader = new ExperienceLoader(href, instanceId, this.fullNamespace, this.store, experienceType);

            // Get the connector for the child experience, and add it to the child connector map
            await loader.loadConnector();
            const { connector: childConnector } = loader;

            // Get the connected component for the child experience.
            await loader.getConnectedExperience({
                // add any override props
                ...(childConnectorProps || {}),
                // get core connector props
                ...this.getChildExperienceConnectorProps(experienceType, instanceId)
            });

            // Get the initial component instance for the child. The experience connector can get a new instance if it needs to do special
            // processing (i.e. passing in OwnProps)
            childConnector.getNewComponentInstance();

            return childConnector;
        } catch (error) {
            // Remove this config from cache.
            resolver.removeCachedConfig(configInfo.href);

            // Log an app error if the loader failed to load for whatever reason
            /// NOTE: DO NOT throw Error or reject promise here, or else the parent might not load the remaining child experiences.
            TelemetryBase.trackAppErrorEvent({
                ...ComponentConnectorAppErrors.BundleLoadError,
                message: `Failed to load experience: ${JSON.stringify(configInfo)}, with error: ${error}`
            });

            return null;
        }
    }

    /**
     * Load a single data connector
     * @param configInfo - data connector config info object
     * @returns {Promise{void}} - the async operation that denotes loading of all the children.
     */
    private async loadDataConnector(configInfo: ChildExperienceConfigInfo): Promise<void> {

        // Get an experience loader instance for the child experience
        if (!configInfo || !configInfo.href) {

            // Log an app error if the loader failed to load for whatever reason
            TelemetryBase.trackAppErrorEvent({
                ...ComponentConnectorAppErrors.BundleLoadError,
                message: `Data connector loading failed: ${JSON.stringify(configInfo)}`
            });

            return;
        }

        const { experienceType } = configInfo;

        try {
            const loader = new ExperienceLoader(
                // if it's a config-less experience type, do not pass in the HREF to prevent loading the config.
                // TODO: after deployment, we need to update configs of config-less connectors so that we don't need
                //      a special check like this, and instead the empty HREF can be directly set in the config.
                //      Need to do this in a phased approach so that we don't break PROD users with old scripts.
                experienceType && ConfiglessConnectors[experienceType]
                    ? ""
                    : configInfo.href,
                "",
                "",
                PageBase.getInstance().store,
                experienceType
            );

            // Get the connector for the child experience, and add it to the child connector map
            await loader.loadConnector(true);
        } catch (error) {
            // Log an app error if the loader failed to load for whatever reason
            /// NOTE: DO NOT throw Error or reject promise here, or else the parent might not load the remaining child experiences.
            TelemetryBase.trackAppErrorEvent({
                ...ComponentConnectorAppErrors.BundleLoadError,
                message: `Failed to load data connector: ${JSON.stringify(configInfo)}, with error: ${error}`
            });
        }
    }

    /**
     * Select and return the proper inner state for the component. The base DataConnector manages the wrapping of the state
     * and so it's getCurrentState() method is used to get the inner state (TState) for the connected component
     * NOTE: The signature of this component requires a state parameter, so that it can be passed into the subspaced()
     * method that is used to connect the component to state
     * @param state For this implementation, this parameter is ignored, since the actual state is managed by the base DataConnector
     */
    private selectComponentState(state: TParentState): TState {
        return (this.getCurrentState());
    }

    /**
     * Start lifecycle of a child experience
     * @param configInfo - the child experience config info
     * @param reRenderCallback - Render callback to call when dependencies are resolved
     * @param childConnectorProps - child experience connector props
     * @returns {Promise{void}} - the async operation that denotes loading of the child.
     */
    private async startProcessingChildExperience(
        configInfo: ChildExperienceConfigInfo,
        reRenderCallback: () => void,
        childConnectorProps?: ExperienceInitialProps): Promise<void> {
        const milestoneDependencyToLoadChildExp = getExperienceLoadDelegate(configInfo.experienceType);

        if (milestoneDependencyToLoadChildExp && milestoneDependencyToLoadChildExp.shouldResolve) {
            // Don't load delayed children on server side to avoid HTML mismatches
            if (canUseDOM()) {
                let addTimeout = true;

                if (milestoneDependencyToLoadChildExp.deferralType === DeferralEventType.CustomEventBasedDeferral) {
                    addTimeout = false;
                    configInfo.state = ExperienceState.Unknown;
                }

                this.resolveDependency(configInfo, milestoneDependencyToLoadChildExp.shouldResolve, () => {
                    this.loadAndRenderExperience(configInfo, reRenderCallback, true, childConnectorProps);
                }, addTimeout);
            }
        } else {
            await this.loadAndRenderExperience(configInfo, reRenderCallback, false, childConnectorProps);
        }
    }

    /**
     * load and render experience
     * @param childExpInfo - the child experience config info
     * @param reRenderCallback - Render callback to call when dependencies are resolved
     * @param delayLoaded - whether this experience is delay loaded or not
     * @param childConnectorProps - child experience connector props
     * @returns {Promise{void}} - the async operation that denotes loading of the child.
     */
    private async loadAndRenderExperience(
        childExpInfo: ChildExperienceConfigInfo,
        reRenderCallback: () => void,
        delayLoaded: boolean,
        childConnectorProps?: ExperienceInitialProps): Promise<void> {
        // Get an experience loader instance for the child experience
        if (!childExpInfo
            || !childExpInfo.href
            || !childExpInfo.instanceId) {

            // Log an app error if the loader failed to load for whatever reason
            TelemetryBase.trackAppErrorEvent({
                ...ComponentConnectorAppErrors.BundleLoadError,
                message: `Attempted to load child with invalid experience config: ${JSON.stringify(childExpInfo)}`
            });

            return;
        }

        try {
            const connector = await this.getChildExperienceConnector(childExpInfo, childConnectorProps);

            if (!connector) {
                childExpInfo.state = ExperienceState.Failed;
                return;
            }

            const milestoneDependencyToRenderChildExp = getExperienceRenderDelegate(childExpInfo.experienceType);

            if (milestoneDependencyToRenderChildExp && milestoneDependencyToRenderChildExp.shouldResolve) {
                this.resolveDependency(childExpInfo, milestoneDependencyToRenderChildExp.shouldResolve, () => {
                    childExpInfo.state = ExperienceState.ReadyToRender;
                    this.addConnectorToChildMap(childExpInfo, connector);
                    reRenderCallback();
                }, true);
            } else {
                childExpInfo.state = ExperienceState.ReadyToRender;
                this.addConnectorToChildMap(childExpInfo, connector);
                if (delayLoaded) {
                    reRenderCallback();
                }
            }
        } catch (error) {
            childExpInfo.state = ExperienceState.Failed;
            // Log an app error if the loader failed to delayload
            TelemetryBase.trackAppErrorEvent({
                ...ComponentConnectorAppErrors.ChildDelayLoadError,
                message: `Attempted to delay load child ${JSON.stringify(childExpInfo.experienceType)} and failed as associated deferral promise returned an error : ${JSON.stringify(error)}`
            });
        }
    }

    /**
     * add connector to child map
     * @param configInfo - the child experience config info
     * @param childConnector - child experience connector
     */
    private addConnectorToChildMap(configInfo: ChildExperienceConfigInfo, childConnector: IComponentConnector<any, any, any>): void {
        // If the experience loaded, and we got the connected component, then add it to the child map,
        // as long as we have not seen this child before
        if (!this.childMap.has(configInfo.instanceId)) {
            this.childMap.set(configInfo.instanceId, childConnector);
        } else {
            // Log an app error
            TelemetryBase.trackAppErrorEvent({
                ...ComponentConnectorAppErrors.DuplicateChildInstanceFound,
                message: `Duplicate child instance found: ${childConnector.namespace}`,
                pb: {
                    ...ComponentConnectorAppErrors.DuplicateChildInstanceFound.pb,
                    customMessage: `href: ${location && location.href}, parentNamespace: ${this.fullNamespace}`
                }
            });
        }
    }

    /**
     * Check if delay loaded experiences are ready to be rendered
     * @param children - the child experience config info array
     */
    private readyExperienceToRender(children: ChildExperienceConfigInfo[]): void {
        const config = this.config as BaseExperienceConfig<any>;
        let reRender = true;

        if (!config.childExperiencesRenderType || config.childExperiencesRenderType === ChildExperiencesDeferRenderType.batch) {
            for (const child of children) {
                if (child.state !== ExperienceState.ReadyToRender && child.state !== ExperienceState.Failed && child.state !== ExperienceState.Unknown) {
                    reRender = false;
                }
            }
        }

        if (reRender) {
            this.reRenderExperience();
        }
    }

    /**
     * Resolves promise dependency with applicable timeout strategy
     * @param dependency - Delegate Promise to resolve dependency
     * @param callback - callback function to call when depdendency is resolved
     */
    private resolveDependency(configInfo: ChildExperienceConfigInfo,
        dependency: () => Promise<boolean>,
        callback: () => void,
        addTimeout: boolean): void {
        let pending = true;
        let timer: NodeJS.Timer = null;

        if (addTimeout) {
            timer = setTimeout(() => {
                if (pending) {
                    pending = false;
                    callback();
                    TelemetryBase.trackEvent({
                        ...ComponentConnectorAppErrors.ExperienceDependencyTimeout,
                        message: `Milestone depdendency failed to resolve. Timeout occured for ${JSON.stringify(configInfo.experienceType)}`
                    });
                }
            }, this.$maxWaitToBatchRenderChildExperiences);
        }

        dependency().then(() => {
            if (addTimeout) {
                clearTimeout(timer);
            }

            if (pending) {
                pending = false;
                callback();
            }
        });
    }
}
